import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { CTAButton, MaskedLines, ParallaxImage, Reveal, SectionTag } from "../components/ui-bits";
import { IMAGES, SERVICES } from "../content";

export default function Services() {
    return (
        <div data-testid="services-page">
            <section className="blueprint-grid border-b border-inkline">
                <div className="mx-auto max-w-[1440px] px-6 pb-16 pt-16 md:px-10 lg:pb-24 lg:pt-24">
                    <SectionTag>Services</SectionTag>
                    <MaskedLines
                        lines={["SIGNS THAT PASS", "EVERY INSPECTION."]}
                        className="font-display text-[13vw] font-bold leading-[0.9] tracking-tighter sm:text-7xl lg:text-[6rem]"
                        accentIndexes={[1]}
                    />
                    <Reveal delay={0.55}>
                        <p className="mt-8 max-w-xl text-base leading-relaxed text-smoke md:text-lg">
                            Fabrication, braille, room ID, wayfinding, restroom signage, and full-building audits —
                            every deliverable verified against ADA §703 and CBC 11B before it reaches your wall.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section data-testid="services-list" className="border-b border-inkline">
                <div className="mx-auto max-w-[1440px] px-6 md:px-10">
                    {SERVICES.map((s, i) => (
                        <Reveal key={s.n}>
                            <div
                                data-testid={`service-row-${i}`}
                                className={`grid grid-cols-1 gap-10 py-16 md:py-20 lg:grid-cols-12 lg:gap-6 ${i !== 0 ? "border-t border-inkline" : ""}`}
                            >
                                <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                                    {s.image ? (
                                        <div className="relative">
                                            <div className={`absolute h-full w-full ${i % 2 === 1 ? "-right-4" : "-left-4"} -top-4 bg-volt`} aria-hidden="true" />
                                            <ParallaxImage
                                                src={IMAGES[s.image]}
                                                alt={s.title}
                                                className="relative aspect-[4/3] w-full border border-inkline"
                                                speed={0.08}
                                            />
                                        </div>
                                    ) : (
                                        <div className={`relative flex aspect-[4/3] w-full items-center justify-center border border-inkline ${i % 2 === 1 ? "bg-ink text-bone" : "bg-bluebrand text-white"}`}>
                                            <p className="px-8 text-center font-display text-4xl font-bold leading-tight tracking-tighter md:text-5xl">
                                                {s.blurb}
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <div className={`flex flex-col justify-center lg:col-span-7 ${i % 2 === 1 ? "lg:order-1 lg:pr-12" : "lg:pl-12"}`}>
                                    <p className="font-display text-sm font-bold text-smoke">{s.n}</p>
                                    <h2 className="mt-3 font-display text-3xl font-bold tracking-tight md:text-5xl">{s.title}</h2>
                                    <p className="mt-2 text-sm font-bold uppercase tracking-widest text-bluebrand">{s.blurb}</p>
                                    <p className="mt-5 max-w-xl text-base leading-relaxed text-smoke md:text-lg">{s.desc}</p>
                                    <div className="mt-6 flex flex-wrap gap-2">
                                        {s.refs.map((r) => (
                                            <span key={r} className="border border-inkline px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-smoke">
                                                {r}
                                            </span>
                                        ))}
                                    </div>
                                    <Link
                                        to="/get-started"
                                        data-testid={`service-cta-${i}`}
                                        className="group mt-8 inline-flex w-fit items-center gap-2 font-display text-sm font-bold uppercase tracking-widest text-ink transition-colors duration-300 hover:text-bluebrand"
                                    >
                                        Request this service
                                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                                    </Link>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="bg-ink text-bone">
                <div className="mx-auto flex max-w-[1440px] flex-col items-start justify-between gap-8 px-6 py-20 md:flex-row md:items-center md:px-10">
                    <Reveal>
                        <h2 className="font-display text-4xl font-bold leading-none tracking-tight md:text-6xl">
                            Not sure what <span className="text-volt">you need?</span>
                        </h2>
                        <p className="mt-4 max-w-md text-sm text-bone/70 md:text-base">
                            Most clients aren't. Book a free audit and we'll hand you the full list — violations,
                            fixes, and pricing.
                        </p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <CTAButton to="/get-started" variant="volt" testid="services-cta-audit">
                            Book a free audit
                        </CTAButton>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
